import { useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';

import { defaultProjectFilters } from 'features/ReviseProjectProposal/defaultValues';
import { filtersValidationSchema } from 'features/ReviseProjectProposal/validation';

import {
  CurrencyInput,
  MultipleSelect,
  TextareaInput,
  TextInput,
} from 'components/FormControl';
import { TOAST } from 'constant';
import { toast } from 'layout';

const formOptions = { resolver: yupResolver(filtersValidationSchema) };

function SearchFilters({
  visible,
  setVisible,
  filters,
  setFilters,
  appliedfilters,
  setAppliedFilters,
  organizations,
}) {
  // #region Data
  const location = useLocation();
  const { t } = useTranslation();

  const { control, handleSubmit, reset, trigger, setValue } = useForm({
    mode: 'onChange',
    defaultValues: { projectStatus: ['doing'] },
    ...formOptions,
    shouldFocusError: true,
  });
  const { errors, isDirty, dirtyFields } = useFormState({
    control,
  });
  // #endregion Data

  // #region Event
  useEffect(() => {
    reset(filters);
    reset(appliedfilters);
    setValue('signedAt.start', filters?.signedAt?.start ? new Date(filters?.signedAt?.start) : '');
    setValue('signedAt.end', filters?.signedAt?.end ? new Date(filters?.signedAt?.end) : '');
  }, [filters, appliedfilters]);

  const onSubmit = (data) => {
    const modifiedTotalExpenditure = {
      min: data?.totalExpenditure?.min > 0 ? data.totalExpenditure.min : undefined,
      max: data?.totalExpenditure?.max > 0 ? data.totalExpenditure.max : undefined,
    };
    const submitData = { ...data, totalExpenditure: modifiedTotalExpenditure };

    localStorage.setItem(`admin-${location.pathname.slice(1)}-filters`, JSON.stringify(submitData));

    setFilters(submitData);
    setAppliedFilters(submitData);
    setVisible(false);
  };

  const onClear = () => {
    reset({});
    localStorage.setItem(
      `admin-${location.pathname.slice(1)}-filters`,
      JSON.stringify(defaultProjectFilters)
    );
    setVisible(false);
    setFilters(defaultProjectFilters);
    setAppliedFilters(defaultProjectFilters);
  };

  const handleTotalExpenditureChange = async () => {
    await trigger('totalExpenditure.min');
    await trigger('totalExpenditure.max');
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
  };

  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };
  // #endregion Event

  // #region Render
  return (
    <Dialog
      header={<p>{t('projectProposal.viewList.searchFilters')}</p>}
      visible={visible}
      position="center"
      className="w-11 md:w-8 lg:w-8 mx-1rem"
      onHide={() => setVisible(false)}
      draggable={false}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <form
        className="p-fluid"
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={checkKeyDown}
        autoComplete="off"
      >
        <div className="grid">
          {/* vietnameseName */}
          <div className="col-12 md:col-6 lg:col-6">
            <TextareaInput
              label={t('projectProposal.viewList.name')}
              name="vietnameseName"
              control={control}
              errors={errors}
            />
          </div>
          {/* ownerName */}
          <div className="col-12 md:col-6 lg:col-6">
            <TextInput
              label={t('projectProposal.viewList.owner')}
              name="ownerName"
              control={control}
              errors={errors}
            />
          </div>
          {/* organizationId */}
          <div className="col-12 md:col-6 lg:col-4">
            <MultipleSelect
              label={t('projectProposal.viewList.organization')}
              name="organizationId"
              options={organizations}
              inputField="id"
              renderField="name"
              control={control}
              errors={errors}
            />
          </div>
          {/* totalExpenditure */}
          <div className="col-12 md:col-6 lg:col-4">
            <label htmlFor="totalExpenditure.min">
              {t('projectProposal.viewList.totalExpenditure')}
            </label>
            <div className="flex gap-2 flex-wrap mt-2">
              <div className="flex align-items-center w-9 xl:w-5" style={{ minWidth: '12rem' }}>
                <span className="w-3rem font-italic">{t('projectProposal.viewList.from')}</span>
                <CurrencyInput
                  name="totalExpenditure.min"
                  control={control}
                  errors={errors}
                  onCustomChange={handleTotalExpenditureChange}
                />
              </div>
              <div className="flex align-items-center w-9 xl:w-5" style={{ minWidth: '12rem' }}>
                <span className="w-3rem font-italic">{t('projectProposal.viewList.to')}</span>
                <CurrencyInput
                  name="totalExpenditure.max"
                  control={control}
                  errors={errors}
                  onCustomChange={handleTotalExpenditureChange}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-content-between">
          <Button
            label={t('projectProposal.viewList.clearFilter')}
            type="button"
            className="w-8rem"
            severity="danger"
            onClick={onClear}
          />

          <div className="flex justify-content-end gap-2">
            <Button
              label={t('formLayout.action.close')}
              type="button"
              className="w-7rem"
              severity="danger"
              onClick={() => setVisible(false)}
            />
            <Button
              label={t('employee.viewList.apply')}
              type="submit"
              className="w-7rem"
              severity="info"
              onClick={checkFormValid}
              disabled={!(isDirty && Object.keys(dirtyFields).length)}
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

export default SearchFilters;

SearchFilters.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  appliedfilters: PropTypes.bool.isRequired,
  setAppliedFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  organizations: PropTypes.array,
};

SearchFilters.defaultProps = {
  organizations: [],
};
